import { useEffect, useState, useCallback } from 'react';
import { Box, Grid, Stack, } from '@mui/material';
import Swal from 'sweetalert2';

import NoteAddIcon from '@mui/icons-material/NoteAdd';

import CreateArrivedList from '../components/manage_arrived/CreateArrivedList';
import ArrivedListViewer from '../components/manage_arrived/ArrivedListViewer';
import ArrivedListFolder from '../components/manage_arrived/ArrivedListFolder';
import EditArrivedList from '../components/manage_arrived/EditArrivedList';

import CustomSearch from '../components/custom_components/CustomSearch';
import CustomButton from '../components/custom_components/CustomButton';
import CustomSelect from '../components/custom_components/CustomSelect';
import CustomRefreshButton from '../components/custom_components/CustomRefreshButton';

import SearchNotFound from '../utils/SearchNotFound';
import ListCardSkeleton from '../utils/ListCardSkeleton';

export default function ManageArrived() {

    const apiUrl = process.env.REACT_APP_API_URL;

    const [orderList, setOrderList] = useState([]);
    const [openList, setOpenList] = useState(false);
    const [listInfo, setListInfo] = useState([]);

    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    const [month, setMonth] = useState("");
    const [year, setYear] = useState(new Date().getFullYear());
    const [cargo, setCargo] = useState("");

    const [openCreateList, setOpenCreateList] = useState(false);
    const [openEditList, setOpenEditList] = useState(false);

    const startYear = 2025;
    const endYear = new Date().getFullYear() + 5;
    const years = [];
    for (let y = startYear; y <= endYear; y++) {
        years.push(y);
    }

    // DISPLAY LIST'S FUNCTION
    const handleDisplayOrderList = useCallback(async (lmonth, lyear, lcargo) => {

        setLoading(true);

        try {
            const response = await fetch(
                `${apiUrl}manage_arrived/get_arrived_list.php`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        search: search,
                        month: lmonth,
                        year: lyear,
                        cargo: lcargo,
                    })
                });

            const result = await response.json();

            if (result.status === 'success') {

                setOrderList(result.data || []);

            } else if (result.status === 'error') {

                Swal.fire({
                    title: 'Server Error!',
                    text: result.message,
                    icon: 'error'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error,
                icon: 'error'
            });
            console.error('Server error:', error);
        } finally {
            setLoading(false);
        }

    }, [debouncedSearch]);

    useEffect(() => {
        handleDisplayOrderList(month, year, cargo);
    }, [month, year, cargo, handleDisplayOrderList]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const handleOpenList = (listInfo) => {
        setListInfo(listInfo);
        setOpenList(true);
    };

    const handleEditList = (listInfo) => {
        setListInfo(listInfo);
        setOpenEditList(true);
    };

    return (
        <Box>

            {openList ?

                <ArrivedListViewer
                    onBack={() => setOpenList(false)}
                    listInfo={listInfo}
                />

                :

                <Stack>

                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ width: '100%' }}
                        mb={3}
                    >

                        <Stack
                            direction="row"
                            justifyContent="center"
                            gap={2}
                        >

                            <CustomSearch
                                placeholder='Search by List Name'
                                md='36ch'
                                value={search}
                                onChange={(val) => setSearch(val)}
                            />

                            <CustomRefreshButton
                                label="Refresh"
                                loading={loading}
                                onClick={() => handleDisplayOrderList(month, year, cargo)}
                            />

                            <CustomSelect
                                label="Filter by Month"
                                value={month}
                                minWidth={160}
                                onChange={setMonth}
                                options={[
                                    { label: <em>All</em>, value: "" },
                                    { label: "January", value: "01" },
                                    { label: "February", value: "02" },
                                    { label: "March", value: "03" },
                                    { label: "April", value: "04" },
                                    { label: "May", value: "05" },
                                    { label: "June", value: "06" },
                                    { label: "July", value: "07" },
                                    { label: "August", value: "08" },
                                    { label: "September", value: "09" },
                                    { label: "October", value: "10" },
                                    { label: "November", value: "11" },
                                    { label: "December", value: "12" },
                                ]}
                            />

                            <CustomSelect
                                label="Filter by Year"
                                value={year}
                                minWidth={160}
                                onChange={setYear}
                                options={years.map((y) => ({
                                    label: y,
                                    value: y,
                                }))}
                            />

                            <CustomSelect
                                label="Filter by Cargo"
                                value={cargo}
                                minWidth={160}
                                onChange={setCargo}
                                options={[
                                    { label: <em>All</em>, value: "" },
                                    { label: "Sea Cargo", value: "Sea Cargo" },
                                    { label: "Air Cargo", value: "Air Cargo" },
                                    { label: "PH", value: "PH" },
                                ]}
                            />

                        </Stack>

                        <CustomButton
                            label="Create List"
                            startIcon={<NoteAddIcon />}
                            onClick={() => setOpenCreateList(true)}
                        />

                    </Stack>

                    {/* LOADING FETCH */}
                    {loading ? (

                        <Grid container spacing={2}>
                            {[...Array(6)].map((_, index) => (
                                <Grid
                                    key={index}
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                        md: 4,
                                        lg: 3,
                                    }}
                                >
                                    <ListCardSkeleton />
                                </Grid>
                            ))}
                        </Grid>

                    ) : orderList.length === 0 ? (

                        // EMPTY 
                        <SearchNotFound />

                    ) : (

                        // DISPLAY DATA
                        <Grid container spacing={2}>
                            {orderList.map((list) => (
                                <Grid
                                    key={list.ListID}
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                        md: 4,
                                        lg: 3,
                                    }}
                                >
                                    <ArrivedListFolder
                                        list={list}
                                        openList={handleOpenList}
                                        onEditList={() => handleEditList(list)}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                    )}

                </Stack>
            }

            <CreateArrivedList
                open={openCreateList}
                onClose={() => setOpenCreateList(false)}
                onUpdate={(val) =>
                    setOrderList(prev => [
                        val,
                        ...prev,
                    ])
                }
            />

            <EditArrivedList
                open={openEditList}
                onClose={() => setOpenEditList(false)}

                list={listInfo}
                onUpdate={(val) =>
                    setOrderList(prev =>
                        prev.map(item =>
                            item.ListID === val.ListID
                                ? {
                                    ...item,
                                    ListName: val.listName,
                                    Cargo: val.listCargo,
                                    DateArrived: val.listDate,
                                }
                                : item
                        )
                    )
                }
            />

        </Box>
    );
};


